import {
    ChevronLeft,
    CreditCard,
    ShoppingBag,
    Trash2,
    X,
} from "lucide-react";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { TailSpin } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
    createOrder,
    emptyCart,
    getShoppingCartItems,
    removeShoppingCartItem,
    updateCartProducts
} from "../../api/action/cart";
import { fetchStudent } from "../../api/action/user";
import { setOrder } from "../../api/reducers/orderReducer";
import Loading from "../LandingPage/InstitutionDomain/Enquiry/components/Loading";
import theme from "../LandingPage/InstitutionDomain/components/theme/theme1";
import CartSkeleton from "./CartSkeleton";
import { Alert as UiAlert } from "../../components/ui/Feedback";

const spacingValue = (value) => (typeof value === "number" ? `${value * 8}px` : value);

const sxToStyle = (sx = {}) => {
    const source = typeof sx === "function" ? sx({ zIndex: { drawer: 1200 } }) : sx;
    const style = {};
    Object.entries(source || {}).forEach(([key, value]) => {
        if (key.startsWith("&")) return;
        if (key === "p") style.padding = spacingValue(value);
        else if (key === "px") {
            style.paddingLeft = spacingValue(value);
            style.paddingRight = spacingValue(value);
        } else if (key === "py") {
            style.paddingTop = spacingValue(value);
            style.paddingBottom = spacingValue(value);
        } else if (key === "mt") style.marginTop = spacingValue(value);
        else if (key === "mb") style.marginBottom = spacingValue(value);
        else if (key === "mr") style.marginRight = spacingValue(value);
        else if (key === "ml") style.marginLeft = spacingValue(value);
        else style[key] = value;
    });
    return style;
};

const mergeStyles = (sx, style) => ({ ...sxToStyle(sx), ...(style || {}) });

const ArrowBackIosNew = ({ fontSize, className }) => (
    <ChevronLeft className={className || (fontSize === "small" ? "h-4 w-4" : "h-5 w-5")} aria-hidden="true" />
);
const RemoveShoppingCart = () => <Trash2 className="h-4 w-4" aria-hidden="true" />;
const ShoppingBagOutlined = ({ className }) => <ShoppingBag className={className || "h-12 w-12"} aria-hidden="true" />;
const ShoppingCartCheckoutRounded = () => <CreditCard className="h-5 w-5" aria-hidden="true" />;

const Box = ({ children, sx, style, className, ...props }) => (
    <div className={className} style={mergeStyles(sx, style)} {...props}>{children}</div>
);

const Container = ({ children, maxWidth = "lg", sx, style, className, ...props }) => (
    <div
        className={className}
        style={{
            width: "100%",
            maxWidth: maxWidth === "lg" ? "1024px" : "1280px",
            marginLeft: "auto",
            marginRight: "auto",
            paddingLeft: "16px",
            paddingRight: "16px",
            ...mergeStyles(sx, style),
        }}
        {...props}
    >
        {children}
    </div>
);

const Grid = ({ children, container, item, spacing = 0, xs = 12, md, sx, style, ...props }) => {
    if (container) {
        return (
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                    gap: spacingValue(spacing),
                    ...mergeStyles(sx, style),
                }}
                {...props}
            >
                {children}
            </div>
        );
    }

    const span = md || xs || 12;
    return <div style={{ gridColumn: `span ${span} / span ${span}`, ...mergeStyles(sx, style) }} {...props}>{children}</div>;
};

const Typography = ({ children, variant, className, sx, style, ...props }) => {
    const Component = variant?.startsWith?.("h") ? variant : "p";
    return <Component className={className} style={mergeStyles(sx, style)} {...props}>{children}</Component>;
};

const Button = ({ children, fullWidth, startIcon, endIcon, sx, style, className = "", type = "button", variant, color, size, ...props }) => (
    <button
        type={type}
        className={className}
        style={{
            width: fullWidth ? "100%" : undefined,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            border: variant === "outlined" ? "1px solid currentColor" : "1px solid transparent",
            cursor: props.disabled ? "not-allowed" : "pointer",
            ...mergeStyles(sx, style),
        }}
        {...props}
    >
        {startIcon}
        {children}
        {endIcon}
    </button>
);

const Divider = ({ className, sx, style }) => (
    <hr className={className} style={{ border: 0, borderTop: "1px solid #e5e7eb", ...mergeStyles(sx, style) }} />
);

const Backdrop = ({ children, open, sx, style }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" style={mergeStyles(sx, style)}>
            {children}
        </div>
    );
};

const Snackbar = ({ children, open, sx, style }) => {
    if (!open) return null;
    return (
        <div className="fixed bottom-5 left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2" style={mergeStyles(sx, style)}>
            {children}
        </div>
    );
};

const Alert = ({ children, severity = "info", onClose }) => (
    <div className="relative">
        <UiAlert tone={severity}>{children}</UiAlert>
        {onClose && (
            <button type="button" aria-label="Close alert" className="absolute right-2 top-2 rounded p-1 hover:bg-black/10" onClick={onClose}>
                <X className="h-4 w-4" aria-hidden="true" />
            </button>
        )}
    </div>
);

const Fade = ({ children }) => <>{children}</>;
const Grow = ({ children }) => <>{children}</>;
const Slide = ({ children }) => <>{children}</>;
const Zoom = ({ children }) => <>{children}</>;

const ApplicantCart = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // State initialization
    const [cartData, setCartData] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const tax = 0;
    const [isLoading, setIsLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [alertMsg, setAlertMsg] = useState({
        open: false,
        message: "",
        severity: "error",
    });
    const [balanceAlert, setBalanceAlert] = useState({
        open: false,
        message: "Please pay balance due amount",
        severity: "success",
    });

    // Data from localStorage and URL
    const applicantData = localStorage.getItem("applicantData") ? JSON.parse(localStorage.getItem("applicantData")) : null;
    const applicantId = applicantData?.primaryId || new URLSearchParams(location.search).get('applicantId');
    localStorage.setItem("applicantId", applicantData?.primaryId);
    const shoppingCartId = applicantData?.account?.shoppingCart?.primaryId || new URLSearchParams(location.search).get('shoppingCartId');
    const payBalanceAmount = new URLSearchParams(location.search).get('payBalanceAmount');
    const hasBalance = payBalanceAmount === 'hasBalance';
    const dueAmount = new URLSearchParams(location.search).get('dueAmount');

    // Redux state
    const companyData = useSelector((state) => state.companyReducer.company);
    const applicantInfo = localStorage.getItem("applicantData")
        ? JSON.parse(localStorage.getItem("applicantData"))
        : null;
    const primaryApplicant = applicantInfo?.[0];
    const companyPrimaryId =
        companyData?.primaryId ||
        localStorage.getItem("companyPrimaryId") ||
        primaryApplicant?.company?.primaryId;

    // Constants
    const currencySymbol = "₹";
    // Check for balance due on mount
    useEffect(() => {
        if (hasBalance) {
            setBalanceAlert(prev => ({
                ...prev,
                open: true,
                message: `Please pay balance due amount: ${currencySymbol}${dueAmount || '0'}`
            }));

            const handleBackButton = (e) => {
                e.preventDefault();
                window.history.pushState(null, null, window.location.pathname);
                setBalanceAlert(prev => ({ ...prev, open: true }));
            };

            window.history.pushState(null, null, window.location.pathname);
            window.addEventListener('popstate', handleBackButton);

            return () => {
                window.removeEventListener('popstate', handleBackButton);
            };
        }
    }, [hasBalance, dueAmount, currencySymbol]);

    // Load applicant data on mount
    useEffect(() => {
        const loadStudentData = async () => {
            try {
                const primaryId = new URLSearchParams(location.search).get('applicantId');
                if (!primaryId) return;

                const response = await dispatch(fetchStudent({ primaryId: primaryId }));

                if (response.error) {
                    setAlertMsg({
                        open: true,
                        message: response.error.message || "Failed to fetch student data",
                        severity: "error"
                    });
                    return;
                }

                const studentData = response.payload || response;
                localStorage.setItem("applicantData", JSON.stringify(studentData));

                if (studentData.company?.primaryId) {
                    localStorage.setItem("companyPrimaryId", studentData.company.primaryId);
                }

                // Don't trigger cart loading here - let other useEffects handle it

            } catch (error) {
                setAlertMsg({
                    open: true,
                    message: "Error loading student data",
                    severity: "error"
                });
            }
        };

        // Only load if we haven't loaded this applicant already
        const params = new URLSearchParams(location.search);
        const currentApplicantId = params.get('applicantId');

        if (currentApplicantId) {
            loadStudentData();
        }
    }, [dispatch, location.search]);
    const [isCartLoading, setIsCartLoading] = useState(false);
    const [cartLoadedIds, setCartLoadedIds] = useState(new Set());
    const getShoppingCartDataRef = React.useRef(null);


    // Fetch cart data when applicantId changes
    useEffect(() => {
        // Only load cart if we have proper IDs and haven't loaded this cart already
        if (applicantId && shoppingCartId && !cartLoadedIds.has(shoppingCartId) && !isCartLoading) {
            getShoppingCartDataRef.current?.(shoppingCartId);
        } else if (!applicantId || !shoppingCartId) {
            setAlertMsg({
                open: true,
                message: "Applicant information not found",
                severity: "error"
            });
        }
    }, [applicantId, cartLoadedIds, isCartLoading, shoppingCartId]);

    // Fix 3: Updated getShoppingCartData with call tracking
    const getShoppingCartData = async (cartId) => {
        // Prevent multiple simultaneous calls
        if (isCartLoading || cartLoadedIds.has(cartId)) {
            return;
        }

        setIsCartLoading(true);
        setIsLoading(true);

        try {
            const data = await getShoppingCartItems(cartId);

            if (data && !data.error) {
                setCartData(data[0].productList || []);
                setTotalAmount(data[0].totalPrice || 0);
                localStorage.setItem("applicantCartProducts", JSON.stringify(data[0].productList || []));

                // Mark this cart as loaded
                setCartLoadedIds(prev => new Set(prev).add(cartId));
            } else {
                throw new Error(data?.error?.message || "Failed to load cart data");
            }
        } catch (error) {
            handleError("Failed to load cart data", error);
            setCartData([]);
            setTotalAmount(0);
        } finally {
            setIsCartLoading(false);
            setIsLoading(false);
        }
    };
    getShoppingCartDataRef.current = getShoppingCartData;


    const [apiCallTimers, setApiCallTimers] = useState({});
    const debouncedApiCall = (key, apiCall, delay = 500) => {
        // Clear existing timer
        if (apiCallTimers[key]) {
            clearTimeout(apiCallTimers[key]);
        }

        // Set new timer
        const timer = setTimeout(async () => {
            await apiCall();
            setApiCallTimers(prev => {
                const newTimers = { ...prev };
                delete newTimers[key];
                return newTimers;
            });
        }, delay);

        setApiCallTimers(prev => ({
            ...prev,
            [key]: timer
        }));
    };

    const emptyCartItems = async () => {
        try {
            const data = await emptyCart({
                primaryId: shoppingCartId,
                accountId: applicantData?.account?.primaryId,
                productList: [],
                company: companyData || applicantData?.company || {},
                modifiedBy: applicantData?.email || "Applicant"
            });
            if (data) {
                setCartData([]);
                setTotalAmount(0);
                setAlertMsg({
                    open: true,
                    message: "Cart emptied successfully",
                    severity: "success",
                });
            }
        } catch (error) {
            handleError("Failed to empty cart", error);
        }
    };

    const handleRemoveItem = async (itemId) => {
        try {
            const updatedCart = cartData.filter(item =>
                item.scheduledProduct?.primaryId !== itemId && item.product?.primaryId !== itemId
            );
            const data = await removeShoppingCartItem({
                primaryId: shoppingCartId,
                accountId: applicantData?.account?.primaryId,
                productList: updatedCart,
                totalPrice: updatedCart.reduce((sum, item) => sum + item.totalPrice, 0),
                company: companyData || applicantData?.company || {},
                modifiedBy: applicantData?.email || "Applicant"
            });
            if (data) {
                setCartData(updatedCart);
                updateTotalAmount(updatedCart);
                setAlertMsg({
                    open: true,
                    message: "Item removed successfully",
                    severity: "success",
                });
            }
        } catch (error) {
            handleError("Failed to remove item", error);
        }
    };

    const updateTotalAmount = (items) => {
        const newTotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
        setTotalAmount(newTotal);
    };

    const handleQuantityChange = async (itemId, newQuantity) => {
        // Update UI immediately for better UX
        const updatedCart = cartData.map(item => {
            if ((item.scheduledProduct?.primaryId === itemId) || (item.product?.primaryId === itemId)) {
                return {
                    ...item,
                    quantity: newQuantity,
                    totalPrice: item.price * newQuantity
                };
            }
            return item;
        });

        setCartData(updatedCart);
        updateTotalAmount(updatedCart);

        // Debounce the API call
        debouncedApiCall(`quantity-${itemId}`, async () => {
            try {
                const cartUpdate = {
                    primaryId: shoppingCartId,
                    accountId: applicantData?.account?.primaryId,
                    productList: updatedCart,
                    totalPrice: updatedCart.reduce((sum, item) => sum + item.totalPrice, 0),
                    company: companyData || applicantData?.company || {},
                    modifiedBy: applicantData?.email || "Applicant"
                };

                const data = await updateCartProducts(cartUpdate);
                if (data && data.error) {
                    throw new Error(data.error.message || "Failed to update quantity");
                }
            } catch (error) {
                // Revert changes on error
                getShoppingCartData(shoppingCartId);
                handleError("Failed to update quantity", error);
            }
        }, 10000);
    };
    const handleCheckout = async () => {
        try {
            setLoading(true);
            const lineItemList = cartData.map(item => ({
                quantity: item.quantity,
                totalPrice: item.totalPrice,
                ...(item.scheduledProduct ? {
                    scheduledProduct: {
                        product: {
                            name: item.scheduledProduct.product?.name,
                            code: item.scheduledProduct.product?.code,
                            primaryId: item.scheduledProduct.product?.primaryId,
                        },
                        primaryId: item.scheduledProduct.primaryId,
                    }
                } : {
                    product: {
                        code: item.product?.code,
                        name: item.product?.name,
                        primaryId: item.product?.primaryId,
                    }
                })
            }));

            localStorage.setItem("applicantFeePayment", "true");
            localStorage.setItem("applicantUserId", applicantId);
            localStorage.setItem("applicantName", `${applicantData?.firstName || ''} ${applicantData?.lastName || ''}`);
            localStorage.setItem("applicantTotalAmount", totalAmount.toString());

            const orderRequest = {
                createdBy: "admin",
                modifiedBy: "admin",
                accountId: localStorage.getItem("accountId"),
                totalLineItems: cartData.length,
                totalAmount: totalAmount,
                subtotalAmount: totalAmount,
                shippingAndHandlingAmount: 0,
                deliveryType: "BRICK_MORTAR",
                tax: 0,
                lineItemList,
                company: {
                    primaryId: companyPrimaryId,
                    domain: {
                        type: "INSTITUTION",
                    },
                },
            };

            const orderResponse = await createOrder(orderRequest);
            if (orderResponse) {
                localStorage.setItem("applicantOrderId", orderResponse.primaryId);
                dispatch(setOrder(orderResponse));
                navigate("/order-history");
            }
        } catch (error) {
            handleError("Checkout failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleError = (message, error) => {
        setAlertMsg({
            open: true,
            message: `${message}: ${error.message || error}`,
            severity: "error",
        });
    };

    const handleCloseAlert = () => {
        setAlertMsg(prev => ({ ...prev, open: false }));
    };

    const handleCloseBalanceAlert = () => {
        setBalanceAlert(prev => ({ ...prev, open: false }));
    };

    const handleBackNavigation = () => {
        if (hasBalance) {
            setBalanceAlert(prev => ({ ...prev, open: true }));
            return;
        }
        navigate(-1);
    };

    return (
        <>
            <br /><br /><br />
            {loading && <Loading message="Processing your order..." />}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="py-12 px-4 bg-gradient-to-r from-blue-50 to-gray-100 font-sans relative overflow-hidden min-h-screen"
                style={{ fontFamily: theme.fontFamily }}
            >
                {/* Balance Due Snackbar - Placed at the very top */}
                <Snackbar
                    open={balanceAlert.open}
                    autoHideDuration={6000}
                    onClose={handleCloseBalanceAlert}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    TransitionComponent={Grow}
                    sx={{
                        marginTop: '80px',
                        '& .alert-root': {
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                        }
                    }}
                >
                    <Alert
                        elevation={6}
                        onClose={handleCloseBalanceAlert}
                        severity={balanceAlert.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {balanceAlert.message}
                    </Alert>
                </Snackbar>

                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center mb-8">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg cursor-pointer hover:bg-blue-700 transition duration-200 ease-in-out mr-4 text-lg flex items-center shadow-md"
                            onClick={handleBackNavigation}
                        >
                            <ArrowBackIosNew fontSize="small" />
                            <span className="ml-1">Back</span>
                        </motion.button>
                        <Typography variant="h4" className="font-bold text-gray-800">
                            Your Application Cart
                        </Typography>
                    </div>

                    <Box className="overflow-auto">
                        <Backdrop open={isLoading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                            <TailSpin stroke="#3498DB" speed={0.8} />
                        </Backdrop>

                        <Container maxWidth="lg">
                            <Snackbar
                                open={alertMsg.open}
                                autoHideDuration={6000}
                                onClose={handleCloseAlert}
                                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                                TransitionComponent={Slide}
                            >
                                <Alert
                                    elevation={6}
                                    onClose={handleCloseAlert}
                                    severity={alertMsg.severity}
                                    variant="filled"
                                    sx={{ width: '100%' }}
                                >
                                    {alertMsg.message}
                                </Alert>
                            </Snackbar>

                            {cartData.length > 0 ? (
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={7}>
                                        <Box className="flex justify-between items-center mb-6">
                                            <Typography variant="h5" className="font-bold text-gray-700">
                                                {cartData.length} {cartData.length === 1 ? 'Item' : 'Items'} in Your Cart
                                            </Typography>
                                            <motion.div whileHover={{ scale: 1.05 }}>
                                                <Button
                                                    onClick={emptyCartItems}
                                                    variant="outlined"
                                                    color="error"
                                                    size="medium"
                                                    startIcon={<RemoveShoppingCart />}
                                                    className="shadow-sm"
                                                >
                                                    Empty Cart
                                                </Button>
                                            </motion.div>
                                        </Box>

                                        <Box className="space-y-4">
                                            {cartData.map((item, index) => (
                                                <Grow in={true} timeout={index * 150} key={index}>
                                                    <div>
                                                        <CartSkeleton
                                                            item={item.scheduledProduct?.product || item.product}
                                                            currency={currencySymbol}
                                                            quantity={item.quantity}
                                                            domain="APPLICANT"
                                                            handleRemove={handleRemoveItem}
                                                            setIncrease={(id) => handleQuantityChange(id, item.quantity + 1)}
                                                            setDecrease={(id) => handleQuantityChange(id, Math.max(1, item.quantity - 1))}
                                                            id={item.scheduledProduct?.primaryId || item.product?.primaryId}
                                                            period={item.scheduledProduct?.period}
                                                            schedule={item.scheduledProduct?.schedule}
                                                        />
                                                    </div>
                                                </Grow>
                                            ))}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={5}>
                                        <Fade in={true} timeout={500}>
                                            <Box className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 sticky top-4">
                                                <Typography variant="h5" className="font-bold text-center mb-6 text-blue-800">
                                                    Order Summary
                                                </Typography>

                                                <Box className="space-y-4 mb-6">
                                                    <Box className="flex justify-between">
                                                        <Typography variant="body1">Subtotal:</Typography>
                                                        <Typography variant="body1" className="font-medium">
                                                            {currencySymbol} {totalAmount.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                    <Box className="flex justify-between">
                                                        <Typography variant="body1">Tax:</Typography>
                                                        <Typography variant="body1" className="font-medium">
                                                            {currencySymbol} {tax.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                    <Divider className="my-2" />
                                                    <Box className="flex justify-between">
                                                        <Typography variant="h6" className="font-bold">
                                                            Total:
                                                        </Typography>
                                                        <Typography variant="h6" className="font-bold text-green-600">
                                                            {currencySymbol} {(totalAmount + tax).toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <motion.div
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <Button
                                                        id="proceed-to-payment-button"
                                                        fullWidth
                                                        variant="contained"
                                                        color="primary"
                                                        size="large"
                                                        onClick={handleCheckout}
                                                        endIcon={<ShoppingCartCheckoutRounded />}
                                                        className="font-bold py-3 text-lg shadow-lg"
                                                        disabled={loading}
                                                    >
                                                        {loading ? 'Processing...' : 'Proceed to Payment'}
                                                    </Button>
                                                </motion.div>

                                                <Typography variant="body2" className="mt-4 text-center text-gray-500">
                                                    By placing your order, you agree to our terms and conditions
                                                </Typography>
                                            </Box>
                                        </Fade>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Box className="text-center py-16">
                                    <Zoom in={true}>
                                        <Box>
                                            <ShoppingBagOutlined className="text-gray-400 text-6xl mb-6 opacity-75" />
                                            <Typography variant="h4" className="mb-4 text-gray-600 font-medium">
                                                Your cart is empty
                                            </Typography>
                                            <Typography variant="body1" className="mb-8 text-gray-500 max-w-md mx-auto">
                                                Continue browsing to find courses or products to add to your application
                                            </Typography>
                                            <motion.div
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="large"
                                                    onClick={() => navigate("/applicant/products")}
                                                    className="font-bold px-8 py-3 shadow-md"
                                                >
                                                    Browse Products
                                                </Button>
                                            </motion.div>
                                        </Box>
                                    </Zoom>
                                </Box>
                            )}
                        </Container>
                    </Box>
                </div>
            </motion.div>
        </>
    );
};

export default ApplicantCart;
